import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, FlowingWaves } from '@/components/feature/DrawingLines';

// ── Data ──────────────────────────────────────────────────────────────────────

const successCases = [
  {
    id: 1,
    storeName: '선율 청담 플래그십',
    owner: '김민준 대표',
    location: '서울 강남구 청담',
    openDate: '2023.03',
    area: '15평',
    initialInvestment: '4,800만 원',
    monthlyRevenue: '2,800만 원',
    roi: '2개월',
    image: 'https://public.readdy.ai/ai/img_res/93b23522fb9aa2eee9f586d1f91c8e76.jpg',
    quote: '본사의 체계적인 지원 시스템 덕분에 첫 달부터 흑자 전환이 가능했습니다.',
    tags: ['Type B 프리미엄', '15평', '월 매출 2,800만 원'],
  },
  {
    id: 2,
    storeName: '선율 홍대점',
    owner: '이소연 원장',
    location: '서울 마포구 홍대',
    openDate: '2023.08',
    area: '8평',
    initialInvestment: '3,600만 원',
    monthlyRevenue: '1,850만 원',
    roi: '3개월',
    image: 'https://readdy.ai/api/search-image?query=cozy%20minimalist%20tattoo%20studio%20interior%20Hongdae%20Seoul%20warm%20lighting%20white%20walls%20small%20but%20elegant%20space%20modern%20design%20clean%20aesthetic%20professional%20photography&width=700&height=480&seq=cases_store2&orientation=landscape',
    quote: '소규모 공간도 본사 인테리어팀이 갤러리처럼 바꿔주셨어요.',
    tags: ['Type A 실속형', '8평', '월 매출 1,850만 원'],
  },
  {
    id: 3,
    storeName: '선율 부산 해울대점',
    owner: '박지훈 대표',
    location: '부산 해울구',
    openDate: '2024.01',
    area: '12평',
    initialInvestment: '4,200만 원',
    monthlyRevenue: '2,200만 원',
    roi: '2.5개월',
    image: 'https://readdy.ai/api/search-image?query=premium%20tattoo%20studio%20interior%20Busan%20Korea%20ocean%20view%20modern%20design%20white%20and%20gold%20accents%20spacious%20consultation%20area%20elegant%20lighting%20professional%20photography&width=700&height=480&seq=cases_store3&orientation=landscape',
    quote: '지방에서도 프리미엄 브랜드의 파워를 실감하고 있습니다.',
    tags: ['Type B 프리미엄', '12평', '월 매출 2,200만 원'],
  },
  {
    id: 4,
    storeName: '선율 대구 동성로점',
    owner: '최유진 원장',
    location: '대구 중구 동성로',
    openDate: '2024.05',
    area: '10평',
    initialInvestment: '2,800만 원',
    monthlyRevenue: '1,650만 원',
    roi: '2개월',
    image: 'https://readdy.ai/api/search-image?query=modern%20tattoo%20studio%20interior%20Daegu%20Korea%20downtown%20location%20sleek%20black%20design%20artistic%20atmosphere%20professional%20workspace%20clean%20minimal%20aesthetic%20photography&width=700&height=480&seq=cases_store4&orientation=landscape',
    quote: '자율시공형으로 시작했지만 븸사 감리 덕분에 퀄리티는 최고입니다.',
    tags: ['Type C 자율시공', '10평', '월 매출 1,650만 원'],
  },
  {
    id: 5,
    storeName: '선율 강남역점',
    owner: '정태양 대표',
    location: '서울 서초구 강남역',
    openDate: '2024.09',
    area: '11평',
    initialInvestment: '4,500만 원',
    monthlyRevenue: '2,450만 원',
    roi: '2개월',
    image: 'https://readdy.ai/api/search-image?query=high%20end%20tattoo%20studio%20interior%20Gangnam%20Seoul%20premium%20location%20sophisticated%20design%20marble%20accents%20warm%20ambient%20lighting%20professional%20photography%20wide%20angle&width=700&height=480&seq=cases_store5&orientation=landscape',
    quote: '유동인구 많은 상권에서 브랜드 인지도가 큰 도움이 됩니다.',
    tags: ['Type B 프리미엄', '11평', '월 매출 2,450만 원'],
  },
  {
    id: 6,
    storeName: '선율 인천 송도점',
    owner: '한미래 원장',
    location: '인천 연수구 송도',
    openDate: '2024.11',
    area: '9평',
    initialInvestment: '3,200만 원',
    monthlyRevenue: '1,520만 원',
    roi: '3개월',
    image: 'https://readdy.ai/api/search-image?query=elegant%20tattoo%20studio%20interior%20Songdo%20Incheon%20modern%20city%20view%20minimalist%20white%20interior%20cozy%20atmosphere%20professional%20lighting%20clean%20design%20photography&width=700&height=480&seq=cases_store6&orientation=landscape',
    quote: '신도십에서도 선율 브랜드로 안정적인 고객층을 확보했어요.',
    tags: ['Type A 실속형', '9평', '월 매출 1,520만 원'],
  },
];

const stats = [
  { value: '47+', label: '전국 가맹점', sub: '2025년 기준' },
  { value: '97%', label: '성공률', sub: '흑자 전환율' },
  { value: '2.5개월', label: '평균 원금 회수', sub: '최단 2개월' },
  { value: '1,850만 원', label: '월 평균 매출', sub: '전 가맹점 평균' },
];

const testimonials = [
  {
    name: '김민준',
    role: '청담 플래그십 대표',
    content: '타투 업계에서 10년 일했지만, 브랜드 파워는 정말 다륍니다. 선율이라는 이름만으로도 고객의 신뢰를 얻을 수 있었어요. 본사 마케팅 지원 덕분에 SNS 팔로워도 3개월 만에 2만 명 돌파했습니다.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Korean%20male%20business%20owner%20portrait%20confident%20smile%20clean%20background%20elegant%20attire%20headshot%20photography&width=120&height=120&seq=cases_avatar1&orientation=squarish',
  },
  {
    name: '이소연',
    role: '홍대점 원장',
    content: '처음에는 8평 작은 공간이 걱정됐는데, 본사 인테리어팀이 공간 활용도를 극대화해주셨어요. 지금은 예약이 밀릴 정도로 바쁩니다. 1인 샵으로 월 1,800만 원 매출은 이전에는 상상도 못했어요.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Korean%20female%20tattoo%20artist%20portrait%20warm%20smile%20clean%20white%20background%20artistic%20vibe%20headshot%20photography&width=120&height=120&seq=cases_avatar2&orientation=squarish',
  },
  {
    name: '박지훈',
    role: '부산 해울대점 대표',
    content: '지방에서 프리미엄 타투 스튜디오를 오픈한다는 게 막막했는데, 본사에서 상권 분석부터 마케팅까지 전부 지원해주셨어요. 지금은 부산에서 선율 찾는 분들이 많아졌습니다.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20Korean%20male%20entrepreneur%20portrait%20friendly%20expression%20clean%20background%20modern%20style%20headshot%20photography&width=120&height=120&seq=cases_avatar3&orientation=squarish',
  },
];

// ── Hero ──────────────────────────────────────────────────────────────────────

function CasesHero() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section className="relative pt-24 md:pt-40 pb-16 md:pb-24 bg-[#1A1A1A] overflow-hidden">
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://readdy.ai/api/search-image?query=successful%20tattoo%20studio%20owner%20celebrating%20opening%20day%20champagne%20toast%20elegant%20black%20interior%20gold%20accents%20premium%20brand%20atmosphere%20cinematic%20wide%20angle%20photography%20Seoul%20Korea&width=1440&height=600&seq=cases_hero_v1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/70" />
      <FlowingWaves color="rgba(201,169,110,0.1)" lineCount={4} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.2)" size={160} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />

      <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-16 text-center">
        <div
          className="flex items-center justify-center gap-2 mb-8 md:mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out' }}
        >
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Franchise
          </Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>성공 사례</span>
        </div>

        <div style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out 0.15s' }}>
          <h1
            className="text-3xl md:text-6xl text-white mb-5 md:mb-6 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em', wordBreak: 'keep-all' }}
          >
            가맹 성공 사례
          </h1>
          <p
            className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto"
            style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8', wordBreak: 'keep-all' }}
          >
            전국 47개 가맹점의 성공 스토리.<br />
            선율과 함께한 점주님들의 실제 경험을 확인하세요.
          </p>
        </div>

        <div className="mx-auto mt-10 md:mt-12 h-px bg-white/15 transition-all duration-1000 ease-out" style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }} />

        <div
          className="mt-8 md:mt-12 grid grid-cols-2 md:flex md:flex-row items-center justify-center gap-6 md:gap-20"
          style={{ opacity: visible ? 1 : 0, transition: 'all 1s ease-out 0.6s' }}
        >
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-xl md:text-3xl text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>{s.value}</p>
              <p className="text-[10px] tracking-[0.15em] md:tracking-[0.2em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif", wordBreak: 'keep-all' }}>{s.label}</p>
              <p className="text-[9px] text-white/20 mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Cases Grid ─────────────────────────────────────────────────────────────────

function CasesGrid() {
  const { ref, visible } = useScrollAnimation(0.05);
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-28 bg-white overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="top-left" color="rgba(201,169,110,0.08)" size={150} />
      <div className="max-w-[1440px] mx-auto px-5 md:px-16">
        <div
          className="text-center mb-8 md:mb-10"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-4 md:mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Success Stories</p>
          <h2
            className="text-2xl md:text-4xl text-[#222222]"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em', wordBreak: 'keep-all' }}
          >
            전국 가맹점 성공 스토리
          </h2>
        </div>
        <div className="mb-8 md:mb-12">
          <GoldLineDivider delay={0.2} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {successCases.map((item, i) => (
            <div
              key={item.id}
              className="group cursor-default"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(50px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
                transitionDelay: `${i * 100 + 200}ms`,
              }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="relative overflow-hidden mb-4 md:mb-5" style={{ height: '240px' }}>
                <img src={item.image} alt={item.storeName} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-transparent to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="text-[10px] px-3 py-1.5 bg-[#C9A96E] text-white font-medium tracking-wide" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.location}
                  </span>
                </div>
                <div
                  className="absolute inset-0 bg-[#1A1A1A]/70 flex items-center justify-center p-6 transition-opacity duration-300"
                  style={{ opacity: hoveredId === item.id ? 1 : 0 }}
                >
                  <p className="text-sm text-white/90 text-center leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif", wordBreak: 'keep-all' }}>
                    "{item.quote}"
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#C9A96E] mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>{item.owner}</p>
                    <h3 className="text-base md:text-lg text-[#222222]" style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.04em', wordBreak: 'keep-all' }}>{item.storeName}</h3>
                  </div>
                  <span className="text-[10px] px-2.5 py-1 bg-[#F5F5F5] text-[#888] rounded-full whitespace-nowrap mt-1 flex-shrink-0 ml-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {item.openDate} 오픈
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4 py-4 border-y border-[#F0F0F0]">
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>창업 비용</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{item.initialInvestment}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>월 매출</p>
                    <p className="text-sm font-semibold text-[#C9A96E]">{item.monthlyRevenue}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>평수</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{item.area}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#AAAAAA] mb-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>원금 회수</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{item.roi}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {item.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2.5 py-1 rounded-full border border-[#EBEBEB] text-[#888] whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ───────────────────────────────────────────────────────────────

function TestimonialsSection() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section className="py-14 md:py-28 bg-[#FAFAFA] overflow-hidden relative" ref={ref}>
      <BotanicalCorner position="bottom-right" color="rgba(201,169,110,0.1)" size={150} />
      <div className="max-w-[1440px] mx-auto px-5 md:px-16">
        <div
          className="text-center mb-8 md:mb-12"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}
        >
          <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-4 md:mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Owner's Voice</p>
          <h2
            className="text-2xl md:text-4xl text-[#222222]"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em', wordBreak: 'keep-all' }}
          >
            점주님들의 생생한 후기
          </h2>
        </div>
        <div className="mb-8 md:mb-12">
          <GoldLineDivider delay={0.2} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className="bg-white border border-[#EBEBEB] p-6 md:p-8"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
                transitionDelay: `${i * 120 + 300}ms`,
              }}
            >
              <div className="flex items-center gap-4 mb-5 md:mb-6">
                <img src={t.avatar} alt={t.name} className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover object-top flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-[#1A1A1A]">{t.name}</p>
                  <p className="text-[11px] text-[#AAAAAA]" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.role}</p>
                </div>
              </div>
              <p className="text-sm text-[#555] leading-relaxed" style={{ fontFamily: "'Noto Sans KR', sans-serif", lineHeight: '1.85', wordBreak: 'keep-all' }}>
                "{t.content}"
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────

function CasesCta() {
  const { ref, visible } = useScrollAnimation(0.2);

  return (
    <section className="py-16 md:py-24 bg-[#1A1A1A] overflow-hidden relative" ref={ref}>
      <FlowingWaves color="rgba(201,169,110,0.08)" lineCount={3} />
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.18)" size={150} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.15)" size={130} />

      <div
        className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-16 text-center"
        style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 1s ease-out' }}
      >
        <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5 md:mb-6" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Your Success Story</p>
        <h2
          className="text-2xl md:text-5xl text-white mb-5 md:mb-6 leading-tight"
          style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.06em', wordBreak: 'keep-all' }}
        >
          다음 성공 스토리의 주인공이<br />
          <span className="text-white/40">되세요.</span>
        </h2>
        <p
          className="text-sm text-white/40 leading-relaxed max-w-md mx-auto mb-8 md:mb-10"
          style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75', wordBreak: 'keep-all' }}
        >
          선율과 함께라면 누구나 성공적인 가맹점 운영이 가능합니다.<br />
          지금 바로 상담을 시작하세요.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 w-full max-w-sm mx-auto sm:max-w-none">
          <Link
            to="/community/inquiry"
            className="w-full sm:w-auto px-10 py-4 bg-[#C9A96E] text-white text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:bg-[#b8955a] transition-colors duration-300 cursor-pointer whitespace-nowrap text-center"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            가맹 문의하기
          </Link>
          <Link
            to="/franchise/guide"
            className="w-full sm:w-auto px-10 py-4 border border-white/20 text-white/60 text-[10px] tracking-[0.2em] uppercase font-medium rounded-full hover:border-white/50 hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            창업 비용 보기
          </Link>
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function FranchiseCasesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <CasesHero />
        <CasesGrid />
        <TestimonialsSection />
        <CasesCta />
      </main>
      <Footer />
    </div>
  );
}